import React from "react";

import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";

import app from "../firebase";

import { getStorage } from "firebase/storage";
import { PostStatusType, SpottPostType } from "../../types/database";
import { Dev } from "../util/consoleUtil";
import firebaseError from "../util/firebaseError";

const db = getFirestore(app);
const storage = getStorage(app);

export const SpottService = {
  One: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [item, setItem] = React.useState<SpottPostType | null>(null);

    const runRequest = async (itemId: string) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "spotts", itemId);
          const mainSnap = await getDoc(mainRef);

          const itemObj = { id: itemId, ...mainSnap.data() } as SpottPostType;
          setLoading(false);

          if (!mainSnap.exists()) return Rej(new Error("Item não existe."));

          setItem(itemObj);

          Ok(itemObj);
        } catch (error) {
          console.log(...new Dev("Request Error [one]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { item, isLoading, runRequest };
  },

  List: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [items, setItems] = React.useState<any[] | null>(null);

    const runRequest = async (periodTimestamp: number) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "spotts");
          const queryRef = query(
            mainRef,
            where("approvedAtTimestamp", ">=", periodTimestamp),
            where("status", "==", "approved"),
            orderBy("approvedAtTimestamp", "desc")
          );
          const mainSnap = await getDocs(queryRef);
          const listData = [] as any;

          mainSnap.forEach((doc) => {
            listData.push({ id: doc.id, ...doc.data() });
          });

          setItems(listData);

          setLoading(false);
          Ok(listData);
        } catch (error) {
          console.log(...new Dev("Request Error [list]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { items, isLoading, runRequest };
  },

  ListLast: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [items, setItems] = React.useState<any[] | null>(null);

    const runRequest = async (postLimit: number = 6) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "spotts");
          const queryRef = query(
            mainRef,
            where("status", "==", "approved"),
            orderBy("approvedAtTimestamp", "desc"),
            limit(postLimit)
          );
          const mainSnap = await getDocs(queryRef);
          const listData = [] as any;

          mainSnap.forEach((doc) => {
            listData.push({ id: doc.id, ...doc.data() });
          });

          setItems(listData);

          setLoading(false);
          Ok(listData);
        } catch (error) {
          console.log(...new Dev("Request Error [list]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { items, isLoading, runRequest };
  },

  LazyList: (definedLimit: number = 5) => {
    const [isLoading, setLoading] = React.useState(false);
    const [hasMore, setHasMore] = React.useState(false);

    const [lastItemRef, setLastItemRef] = React.useState<any>(null);
    const [firstItemRef, setFirstItemRef] = React.useState<any>(null);

    const [items, setItems] = React.useState<any[]>([]);

    const runRequest = async () => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const queryRef = lastItemRef
            ? query(
                collection(db, "spotts"),
                where("status", "==", "approved"),
                orderBy("approvedAtTimestamp", "desc"),
                startAfter(lastItemRef),
                limit(definedLimit)
              )
            : query(
                collection(db, "spotts"),
                where("status", "==", "approved"),
                orderBy("approvedAtTimestamp", "desc"),
                limit(definedLimit)
              );

          const mainSnap = await getDocs(queryRef);

          setLastItemRef(mainSnap.docs[mainSnap.docs.length - 1] || null);

          const listData = [] as any;

          mainSnap.forEach((doc) => {
            listData.push({ id: doc.id, ...doc.data() });
          });

          console.log(listData);

          setItems([...items, ...listData]);

          if (listData.length < definedLimit) setHasMore(false);
          else setHasMore(true);

          if (!firstItemRef && mainSnap.docs.length) setFirstItemRef(mainSnap.docs[0]);

          setLoading(false);

          Ok(listData);
        } catch (error) {
          console.log(...new Dev("Request Error [list]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { items, isLoading, runRequest, hasMore, lastItemRef, firstItemRef };
  },

  ListFromUser: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [items, setItems] = React.useState<any[] | null>(null);

    const runRequest = async (userId: string) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "spotts");
          const queryRef = query(mainRef, where("userId", "==", userId));
          const mainSnap = await getDocs(queryRef);
          const listData = [] as any;

          mainSnap.forEach((doc) => {
            listData.push({ id: doc.id, ...doc.data() });
          });

          setItems(listData);
          setLoading(false);
          Ok(listData);
        } catch (error) {
          console.log(...new Dev("Request Error [listFromUser]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { items, isLoading, runRequest };
  },

  ListStatus: () => {
    const [isLoading, setLoading] = React.useState(false);

    const [items, setItems] = React.useState<any[] | null>(null);

    const runRequest = async (status: PostStatusType) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "spotts");
          const queryRef = query(
            mainRef,
            where("status", "==", status),
            orderBy("createdAtTimestamp", "desc"),
            limit(10)
          );
          const mainSnap = await getDocs(queryRef);
          const listData = [] as any;

          mainSnap.forEach((doc) => {
            listData.push({ id: doc.id, ...doc.data() });
          });

          setItems(listData);

          setLoading(false);
          Ok(listData);
        } catch (error) {
          console.log(...new Dev("Request Error [listStatus]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { items, isLoading, runRequest };
  },

  ListStatusLazy: (definedLimit: number = 5, type: "replace" | "add" = "replace") => {
    const [isLoading, setLoading] = React.useState(false);
    const [hasMore, setHasMore] = React.useState(false);
    const [lastItemRef, setLastItemRef] = React.useState<any>(undefined);
    const [items, setItems] = React.useState<any[] | null>(null);

    const runRequest = async (status: PostStatusType, reset: boolean = false) => {
      return new Promise(async (Ok, Rej) => {
        try {
          if (reset) {
            setItems([]);
            setHasMore(false);
          }

          setLoading(true);

          const mainRef = collection(db, "spotts");
          const queryRef =
            lastItemRef && !reset
              ? query(
                  mainRef,
                  where("status", "==", status),
                  orderBy("createdAtTimestamp", "desc"),
                  startAfter(lastItemRef),
                  limit(definedLimit)
                )
              : query(
                  mainRef,
                  where("status", "==", status),
                  orderBy("createdAtTimestamp", "desc"),
                  limit(definedLimit)
                );

          const mainSnap = await getDocs(queryRef);

          setLastItemRef(mainSnap.docs[mainSnap.docs.length - 1] || null);

          const listData = [] as any;

          mainSnap.forEach((doc) => {
            listData.push({ id: doc.id, ...doc.data() });
          });

          if (type === "add" && !reset) {
            const currentItems = items ? items : [];
            setItems([...currentItems, ...listData]);
          } else {
            setItems(listData);
          }

          if (listData.length < definedLimit) setHasMore(false);
          else setHasMore(true);

          setLoading(false);

          Ok(listData);
        } catch (error) {
          console.log(...new Dev("Request Error [listStatus]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao obter dados. " + firebaseError(error)));
        }
      });
    };

    return { items, isLoading, runRequest, hasMore };
  },

  Create: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (item: SpottPostType) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = collection(db, "spotts");
          const mainSnap = await addDoc(mainRef, item);

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [create]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao criar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  Delete: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (itemId: string) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "spotts", itemId);
          const mainSnap = await deleteDoc(mainRef);

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [delete]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao remover dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  Update: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (itemId: string, payload: any) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "spotts", itemId);
          const mainSnap = await updateDoc(mainRef, payload);

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [update]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao atualizar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  UpdateArrayAdd: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (itemId: string, key: string, payload: any) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "spotts", itemId);
          const mainSnap = await updateDoc(mainRef, { [key]: arrayUnion(payload) });

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [updateArrayAdd]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao atualizar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },

  UpdateArrayRemove: () => {
    const [isLoading, setLoading] = React.useState(false);

    const runRequest = async (itemId: string, key: string, payload: any) => {
      return new Promise(async (Ok, Rej) => {
        try {
          setLoading(true);

          const mainRef = doc(db, "spotts", itemId);
          const mainSnap = await updateDoc(mainRef, { [key]: arrayRemove(payload) });

          setLoading(false);
          Ok(true);
        } catch (error) {
          console.log(...new Dev("Request Error [updateArrayRemove]", error).Error());
          setLoading(false);
          Rej(new Error("Erro ao atualizar dados. " + firebaseError(error)));
        }
      });
    };

    return { isLoading, runRequest };
  },
};
